import { PencilSquareIcon, XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setExtErrors, updateExtraction } from '../../store/extractions/action';
import constants from '../../commonUtils/constants';
import Spinner from '../../components/Spinner';

const ExtractionTitle = () => {
    const dispatch = useDispatch();
    const { extractionData, errors, extLoading, extractionViewOnly } = useSelector((state) => state.Extractions);
    const [isEditing, setIsEditing] = useState(false);
    const [localTitle, setLocalTitle] = useState(extractionData?.title || '');
    const [showSuccess, setShowSuccess] = useState(false);
    const [originalTitle, setOriginalTitle] = useState(extractionData?.title || '');

    // Sync local title with extractionData when not editing
    useEffect(() => {
        if (!isEditing) {
            setLocalTitle(extractionData?.title || '');
            setOriginalTitle(extractionData?.title || '');
        }
    }, [extractionData?.title, isEditing]);

    // Handle successful update - show checkmark for 2 seconds
    useEffect(() => {
        if (!extLoading && !errors?.title && extractionData?.title === localTitle && localTitle !== originalTitle) {
            setOriginalTitle(localTitle);
            setShowSuccess(true);

            const timer = setTimeout(() => setShowSuccess(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [extLoading, errors?.title, extractionData?.title, localTitle, originalTitle]);

    // Handle errors - revert to original and clear error after 2 seconds
    useEffect(() => {
        if (errors?.title) {
            setLocalTitle(originalTitle);
            setIsEditing(false);

            const timer = setTimeout(() => {
                dispatch(setExtErrors(constants.extraction.errorInitialState));
            }, 2000);

            return () => clearTimeout(timer);
        }
    }, [errors?.title, originalTitle, dispatch]);

    const handleBlur = () => {
        setIsEditing(false);

        const trimmedTitle = localTitle.trim();

        if (trimmedTitle === '') {
            setLocalTitle(originalTitle);
        } else if (localTitle !== originalTitle) {
            dispatch(updateExtraction({
                id: extractionData.id,
                formData: { title: localTitle },
                fieldName: 'title'
            }));
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        } else if (e.key === 'Escape') {
            setLocalTitle(originalTitle);
            setIsEditing(false);
        }
    };

    const renderError = (err) => {
        if (!err) return null;
        if (typeof err === 'string') return err;
        return err.message || JSON.stringify(err);
    };

    return (
        <div className="flex items-center gap-2">
            {isEditing ? (
                <input
                    type="text"
                    className="text-xl font-semibold text-slate-900 border border-slate-300 rounded px-2 py-1 focus:outline-none focus:border-blue-500"
                    value={localTitle}
                    onChange={(e) => setLocalTitle(e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    autoFocus
                />
            ) : (
                <>
                    <h1 className="text-xl font-semibold text-slate-900">{extractionData?.title}</h1>
                    {!extractionViewOnly && (extLoading ? (
                        <Spinner className="h-5 w-5" />
                    ) : errors?.title ? (
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                    ) : showSuccess ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    ) : extractionData?.id ? (
                        <PencilSquareIcon
                            className="h-4 w-4 text-slate-400 cursor-pointer hover:text-slate-600"
                            onClick={() => setIsEditing(true)}
                        />
                    ) : null )}
                </>
            )}
            {errors?.title && <small className="text-red-500">{renderError(errors?.title)}</small>}
        </div>
    );
};

export default ExtractionTitle;